import api from './api'
import {
  beginClientSession,
  clearClientSession,
  getLegacyReadableToken,
  getMemoryToken,
  getPersistedToken,
  hasSessionHint,
  markSessionActive,
  readPersistedUser,
  persistUser,
} from '../utils/authSession'

function extractAuthError(error, fallback) {
  let errorMessage = fallback
  if (error.response?.data) {
    const errorData = error.response.data
    if (errorData.error) errorMessage = errorData.error
    else if (errorData.message) errorMessage = errorData.message
    if (errorData.details) {
      console.error('Detalles del error:', errorData.details)
    }
  } else if (error.message) {
    errorMessage = error.message
  }
  return errorMessage
}

async function acceptAuthPayload(response, fallbackError) {
  if (response.data?.success) {
    const { token, user } = response.data.data
    beginClientSession({ token, user })
    return { token, user }
  }
  throw new Error(response.data?.error || fallbackError)
}

export const authService = {
  async login(email, password) {
    try {
      const response = await api.post('/auth/login', { email, password })
      return await acceptAuthPayload(response, 'Error al iniciar sesión')
    } catch (error) {
      const errorMessage = extractAuthError(error, 'Error al iniciar sesión')
      console.error('❌ Error en login:', {
        message: errorMessage,
        status: error.response?.status,
        response: error.response?.data,
      })
      throw new Error(errorMessage)
    }
  },

  async register(userData) {
    const response = await api.post('/auth/register', userData)
    return acceptAuthPayload(response, 'Error al registrar')
  },

  async sendOtp(telefono) {
    const response = await api.post('/auth/send-otp', { telefono })
    if (response.data.success) return response.data
    throw new Error(response.data.error || 'Error al enviar código OTP')
  },

  async loginWithPhone(telefono, otpCode) {
    const response = await api.post('/auth/login-phone', { telefono, otp_code: otpCode })
    return acceptAuthPayload(response, 'Error al iniciar sesión')
  },

  async registerWithPhone(userData) {
    const response = await api.post('/auth/register-phone', userData)
    return acceptAuthPayload(response, 'Error al registrar')
  },

  async socialLogin(provider, providerId, email, nombre, apellido, foto) {
    const response = await api.post('/auth/social-login', {
      provider,
      provider_id: providerId,
      email,
      nombre,
      apellido,
      foto,
    })
    return acceptAuthPayload(response, 'Error al iniciar sesión con ' + provider)
  },

  async forgotPassword(email) {
    const response = await api.post('/auth/forgot-password', { email })
    if (response.data.success) return response.data
    throw new Error(response.data.error || 'Error al solicitar recuperación')
  },

  async resetPassword(token, password, passwordConfirmation) {
    const response = await api.post('/auth/reset-password', {
      token,
      password,
      password_confirmation: passwordConfirmation,
    })
    if (response.data.success) return response.data
    throw new Error(response.data.error || 'Error al restablecer contraseña')
  },

  async changePassword(currentPassword, newPassword, newPasswordConfirmation) {
    const response = await api.post('/auth/change-password', {
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: newPasswordConfirmation,
    })
    if (response.data.success) return response.data
    throw new Error(response.data.error || 'Error al cambiar contraseña')
  },

  async verifyEmail(token) {
    const response = await api.post('/auth/verify-email', { token })
    if (response.data.success) return response.data
    throw new Error(response.data.error || 'Error al verificar email')
  },

  async resendVerification(email) {
    const response = await api.post('/auth/resend-verification', { email })
    if (response.data.success) return response.data
    throw new Error(response.data.error || 'Error al reenviar verificación')
  },

  async addEmail(email) {
    const response = await api.post('/auth/add-email', { email })
    if (response.data.success) return response.data
    throw new Error(response.data.error || 'Error al agregar email')
  },

  async updateProfile(data) {
    const response = await api.post('/auth/profile', data)
    if (response.data.success) {
      const { user } = response.data.data
      persistUser(user)
      return { user }
    }
    throw new Error(response.data.error || 'Error al actualizar perfil')
  },

  async getMe() {
    const response = await api.get('/auth/me')
    if (response.data.success) {
      const userData = response.data.data

      if (userData) {
        const { getAvatarUrl } = await import('../utils/avatarHelpers')
        const normalizedAvatarUrl = getAvatarUrl(userData)
        if (normalizedAvatarUrl) {
          userData.avatar = normalizedAvatarUrl
          userData.foto = normalizedAvatarUrl
        }
        // Restaurar flag de sesión (cookie HttpOnly + bootstrap PWA)
        markSessionActive(true)
        persistUser(userData)
      }

      return userData
    }
    throw new Error(response.data.error || 'Error al obtener usuario')
  },

  async logout() {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      // Aunque falle la red, limpiamos estado local.
      console.warn('Logout remoto falló, limpiando sesión local:', error?.message || error)
    } finally {
      clearClientSession()
    }
    window.location.href = '/login'
  },

  getToken() {
    return getMemoryToken() || getPersistedToken() || getLegacyReadableToken()
  },

  getUser() {
    return readPersistedUser()
  },

  isAuthenticated() {
    return hasSessionHint()
  },
}
